/**
 * Raw color values only — no MUI `Palette` shape, no RN `StyleSheet` shape. Each platform's theme
 * builder (web: `theme/createAppTheme.ts` → `theme/tokens.ts`; mobile: the future RN Paper theme)
 * maps these onto its own palette type. Keeping the source flat means a color changed here changes
 * on both platforms with no risk of the two drifting.
 *
 * ── Design System V2 · Brand ────────────────────────────────────────────────────────────────
 * Willnq's V2 identity is a modern, premium SaaS language:
 *   • Primary  — Royal Purple  (trust + ambition; the brand's signature)
 *   • Secondary— Blue          (calm, informational, links & secondary actions)
 *   • Accent   — Mint          (progress, success highlights, positive momentum)
 *   • Neutral  — Slate         (text, borders, surfaces — cool, never muddy)
 *   • Success  — Green · Warning — Amber · Error — Coral
 * Shades are tuned for WCAG AA contrast on their paired surfaces in both light and dark modes.
 */

export const brandColors = {
  /** Royal Purple — the signature brand color. */
  primary: {
    main: '#7C3AED',
    light: '#A78BFA',
    dark: '#5B21B6',
    contrastText: '#FFFFFF',
  },
  /** Blue — secondary actions, links, informational emphasis. */
  secondary: {
    main: '#2563EB',
    light: '#60A5FA',
    dark: '#1E40AF',
    contrastText: '#FFFFFF',
  },
  /** Mint — accent for progress, momentum and positive highlights. Dark text for AA contrast. */
  accent: {
    main: '#2DD4BF',
    light: '#5EEAD4',
    dark: '#0D9488',
    contrastText: '#053B34',
  },
} as const;

/**
 * Signature gradients for premium surfaces (hero cards, primary CTAs, brand headers). Exposed both
 * as ordered color stops (RN's `expo-linear-gradient` takes a `colors` array + direction) and as a
 * ready-to-use web CSS string, so neither platform re-derives the other's format.
 */
export const brandGradients = {
  primary: {
    stops: ['#7C3AED', '#6D28D9', '#5B21B6'] as const,
    angle: 135,
    css: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 55%, #5B21B6 100%)',
  },
  brand: {
    stops: ['#7C3AED', '#2563EB'] as const,
    angle: 135,
    css: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
  },
  accent: {
    stops: ['#2DD4BF', '#2563EB'] as const,
    angle: 135,
    css: 'linear-gradient(135deg, #2DD4BF 0%, #2563EB 100%)',
  },
  /** Subtle tinted wash for page backgrounds / empty-state canvases. */
  subtleLight: {
    stops: ['#F7F5FF', '#F4F7FF'] as const,
    angle: 160,
    css: 'linear-gradient(160deg, #F7F5FF 0%, #F4F7FF 100%)',
  },
  subtleDark: {
    stops: ['#17141F', '#141320'] as const,
    angle: 160,
    css: 'linear-gradient(160deg, #17141F 0%, #141320 100%)',
  },
} as const;

/**
 * Shared across every status pill in the product (application status, roster status, vendor
 * bench status) so users learn one color language, not several (FRONTEND_GUIDELINES §7.9).
 */
export const statusColors = {
  neutral: '#64748B',
  info: '#2563EB',
  success: '#16A34A',
  warning: '#F59E0B',
  error: '#F43F5E',
} as const;

/**
 * Soft tint pairings for status surfaces (badge backgrounds, alert strips) — a low-alpha wash of
 * each status color that reads as premium rather than the flat saturated fill of enterprise UIs.
 */
export const statusSoftColors = {
  light: {
    neutral: 'rgba(100, 116, 139, 0.12)',
    info: 'rgba(37, 99, 235, 0.12)',
    success: 'rgba(22, 163, 74, 0.12)',
    warning: 'rgba(245, 158, 11, 0.14)',
    error: 'rgba(244, 63, 94, 0.12)',
  },
  dark: {
    neutral: 'rgba(148, 163, 184, 0.18)',
    info: 'rgba(96, 165, 250, 0.18)',
    success: 'rgba(74, 222, 128, 0.18)',
    warning: 'rgba(251, 191, 36, 0.20)',
    error: 'rgba(251, 113, 133, 0.18)',
  },
} as const;

/** Slate neutral scale — cool greys that pair cleanly with the purple brand. */
export const neutralColors = {
  0: '#FFFFFF',
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
} as const;

/**
 * Per-mode surface/text/border colors, layered on top of the neutral scale.
 * `elevated` is the raised-card / popover surface (one step above `surface`); dark mode keeps a
 * deep indigo-slate rather than pure black so it reads premium, never harsh.
 */
export const surfaceColors = {
  light: {
    background: '#F6F7FB',
    surface: neutralColors[0],
    elevated: neutralColors[0],
    border: neutralColors[200],
    borderStrong: neutralColors[300],
    textPrimary: neutralColors[900],
    textSecondary: neutralColors[600],
    textTertiary: neutralColors[400],
    disabled: neutralColors[300],
    hoverOverlay: 'rgba(15, 23, 42, 0.04)',
    selectedOverlay: 'rgba(124, 58, 237, 0.08)',
    focusRing: brandColors.primary.main,
  },
  dark: {
    // V2.1: nudged a step lighter than the original near-black indigo so widget-dense screens
    // (dashboards) don't read as harsh — still a deep, premium ground, never flat black.
    background: '#14111F',
    surface: '#1B1826',
    elevated: '#221F30',
    border: '#322D47',
    borderStrong: '#423C5C',
    textPrimary: '#ECEBF3',
    textSecondary: '#A5A2B8',
    textTertiary: '#6E6A82',
    disabled: '#3A3550',
    hoverOverlay: 'rgba(167, 139, 250, 0.08)',
    selectedOverlay: 'rgba(167, 139, 250, 0.14)',
    focusRing: brandColors.primary.light,
  },
} as const;

export type ThemeMode = keyof typeof surfaceColors;
export type StatusColorKey = keyof typeof statusColors;
export type BrandColorKey = keyof typeof brandColors;
export type BrandGradientKey = keyof typeof brandGradients;
